import core from 'core';
import isDataElementLeftPanel from 'helpers/isDataElementLeftPanel';
import fireEvent from 'helpers/fireEvent';
import { getMaxZoomLevel, getMinZoomLevel } from 'constants/zoomFactors';
import { disableElements, enableElements, setActiveFlyout, setFlyoutToggleElement } from 'actions/internalActions';
import defaultTool from 'constants/defaultTool';
import { PRIORITY_TWO } from 'constants/actionPriority';
import Events from 'constants/events';
import {
  getOpenGenericPanel,
  getEnabledRibbonItems,
  getGenericPanelsOnTheSameLocation,
  isElementOpen,
  getIsCustomUIEnabled,
  getGenericPanels,
  isDisabledViewOnly,
  getVisibleTabPanelTabs
} from 'selectors/exposedSelectors';
import DataElements from 'constants/dataElement';
import { OPACITY_LEVELS } from 'constants/customizationVariables';
import pick from 'lodash/pick';
import { v4 as uuidv4 } from 'uuid';
import selectors from 'selectors';
import checkFeaturesToEnable from 'helpers/checkFeaturesToEnable';
import { getAllAssociatedGroupedItems } from 'helpers/modularUIHelpers';
import { isMobile } from 'helpers/device';
import { isOfficeEditorMode } from 'helpers/officeEditor';
import { areConfigsEquivalent } from 'helpers/compareObjects';
import i18next from 'i18next';
import { panelNames } from 'src/constants/panel';

export const updateViewOnlyBlacklist = (dataElements) => ({
  type: 'UPDATE_VIEW_ONLY_BLACKLIST',
  payload: { dataElements },
});
export const updateViewOnlyWhitelist = (dataElements) => ({
  type: 'UPDATE_VIEW_ONLY_WHITELIST',
  payload: { dataElements },
});
export const closeAllElements = () => (dispatch, getState) => {
  const state = getState();
  const openElements = Object.keys(state.viewer.openElements);
  if (openElements.length > 0) {
    dispatch(closeElements(openElements));
  }
  dispatch(setFlyoutToggleElement(null));
  dispatch(setActiveFlyout(false));
};
export const setScaleOverlayPosition = (position) => ({
  type: 'SET_SCALE_OVERLAY_POSITION',
  payload: { position },
});
export const setDefaultPrintMargins = (margins) => ({
  type: 'SET_DEFAULT_PRINT_MARGINS',
  payload: { margins },
});
export const disableApplyCropWarningModal = () => ({
  type: 'SHOW_APPLY_CROP_WARNING',
  payload: { shouldShowApplyCropWarning: false },
});

export const enableApplyCropWarningModal = () => ({
  type: 'SHOW_APPLY_CROP_WARNING',
  payload: { shouldShowApplyCropWarning: true },
});

export const disableApplySnippingWarningModal = () => ({
  type: 'SHOW_APPLY_SNIPPING_WARNING',
  payload: { shouldShowApplySnippingWarning: false },
});

export const enableApplySnippingWarningModal = () => ({
  type: 'SHOW_APPLY_SNIPPING_WARNING',
  payload: { shouldShowApplySnippingWarning: true },
});

export const setPresetCropDimensions = (presetCropDimensions) => ({
  type: 'SET_PRESET_CROP_DIMENSIONS',
  payload: { presetCropDimensions },
});

export const setPresetNewPageDimensions = (presetNewPageDimensions) => ({
  type: 'SET_PRESET_NEW_PAGE_DIMENSIONS',
  payload: { presetNewPageDimensions },
});

export const setDateTimeFormats = (dateTimeFormats) => ({
  type: 'SET_DATE_TIME_FORMATS',
  payload: { dateTimeFormats },
});

export const setEnableDesktopOnlyMode = (enableDesktopOnlyMode) => ({
  type: 'SET_ENABLE_DESKTOP_ONLY_MODE',
  payload: { enableDesktopOnlyMode },
});

export const setHighContrastMode = (useHighContrastMode) => ({
  type: 'SET_HIGH_CONTRAST_MODE',
  payload: { useHighContrastMode },
});

export const setCanUndo = (canUndo, documentViewerKey = 1) => ({
  type: 'SET_CAN_UNDO',
  payload: { canUndo, documentViewerKey },
});

export const setCanRedo = (canRedo, documentViewerKey = 1) => ({
  type: 'SET_CAN_REDO',
  payload: { canRedo, documentViewerKey },
});

export const setStandardStamps = (t) => async (dispatch) => {
  const rubberStampTool = core.getTool('AnnotationCreateRubberStamp');
  const canvasWidth = 160;
  const canvasHeight = 58;

  const annotations = await rubberStampTool.getStandardStampAnnotations();
  const previews = await Promise.all(
    annotations.map((annotation) => {
      const text = t(`rubberStamp.${annotation['Icon']}`);

      const options = {
        canvasWidth,
        canvasHeight,
        text,
        direction: i18next.dir(),
      };

      return rubberStampTool.getPreview(annotation, options);
    }),
  );

  const standardStamps = annotations.map((annotation, i) => ({
    annotation,
    imgSrc: previews[i],
  }));

  dispatch({
    type: 'SET_STANDARD_STAMPS',
    payload: { standardStamps },
  });
};

export const setCustomStamps = (t) => async (dispatch) => {
  const rubberStampTool = core.getTool('AnnotationCreateRubberStamp');
  const canvasWidth = 160;
  const canvasHeight = 58;

  const annotations = await rubberStampTool.getCustomStampAnnotations();
  await Promise.all(
    annotations.map((annotation) => {
      const text = t(`rubberStamp.${annotation['Icon']}`);

      const options = {
        canvasWidth,
        canvasHeight,
        text,
        direction: i18next.dir(),
      };

      return rubberStampTool.getPreview(annotation, options);
    }),
  );

  const customStampsPromises = annotations.map(async (annotation) => ({
    annotation,
    imgSrc: await annotation.getImageData(),
  }));

  const customStamps = await Promise.all(customStampsPromises);

  dispatch({
    type: 'SET_CUSTOM_STAMPS',
    payload: { customStamps },
  });
};

const stashEnabledRibbons = (ribbonItems) => (
  {
    type: 'STASH_ENABLED_RIBBONS',
    payload: { ribbonItems }
  }
);

export const setReadOnlyRibbons = () => (dispatch, getState) => {
  // Set default toolbar group to View
  dispatch(setToolbarGroup('toolbarGroup-View'));
  let toolbarGroupsToDisable;
  const state = getState();
  if (getIsCustomUIEnabled(state)) {
    // we must remember these toolbar groups to re-enable them when we exit read-only mode
    toolbarGroupsToDisable = getEnabledRibbonItems(state).filter((item) => item !== 'toolbarGroup-View');
    dispatch(stashEnabledRibbons(toolbarGroupsToDisable));
  } else {
    toolbarGroupsToDisable = Object.keys(state.viewer.headers).filter(
      (key) => key.includes('toolbarGroup-') && key !== 'toolbarGroup-View',
    );
  }

  disableElements(toolbarGroupsToDisable, PRIORITY_TWO)(dispatch, getState);
};

export const enableRibbons = () => (dispatch, getState) => {
  const state = getState();
  // There can be a situation where we switch to FormBuilder mode and we get a race condition between setting
  // the active toolbarGroup as what is in the current state and Forms, as redux hasnt dispatched the update to the Forms tool bar yet.
  // We double check here if we are in form mode and set the correct tool bar group
  // We enable ribbons when going into form mode, as we temporarily elevate the user's permissions
  const isInFormFieldCreationMode = core.getFormFieldCreationManager().isInFormFieldCreationMode();
  const toolbarGroup = isInFormFieldCreationMode ? DataElements.FORMS_TOOLBAR_GROUP : state.viewer.toolbarGroup;
  let toolbarGroupsToEnable;
  const isCustomUIDisabled = !getIsCustomUIEnabled(state);
  if (isCustomUIDisabled) {
    dispatch(setToolbarGroup(toolbarGroup || DataElements.ANNOTATE_TOOLBAR_GROUP));
    toolbarGroupsToEnable = Object.keys(state.viewer.headers).filter((key) => key.includes('toolbarGroup-'));
  } else {
    // re-enable the stashed ribbons
    toolbarGroupsToEnable = state.viewer.enabledRibbonsStash;
  }

  enableElements(toolbarGroupsToEnable, PRIORITY_TWO)(dispatch, getState);
};

const isElementDisabled = (state, dataElement) => state.viewer.disabledElements[dataElement]?.disabled;

export const allButtonsInGroupDisabled = (state, toolGroup) => {
  const dataElements = Object.values(state.viewer.toolButtonObjects)
    .filter(({ group }) => group === toolGroup)
    .map(({ dataElement }) => dataElement);

  return dataElements.every((dataElement) => isElementDisabled(state, dataElement));
};

export const setLastActiveToolForRibbon = ({ ribbon, toolName }) => ({
  type: 'SET_LAST_ACTIVE_TOOL_FOR_RIBBON',
  payload: { ribbon, toolName }
});

export const setActiveGroupedItems = (groupedItems) => (dispatch, getState) => {
  if (!groupedItems.length) {
    dispatch(setGroupedItems([]));
    return;
  }

  const state = getState();
  const allAssociatedGroupedItems = getAllAssociatedGroupedItems(state, groupedItems);

  dispatch(setGroupedItems(allAssociatedGroupedItems));
};

const setGroupedItems = (groupedItems) => ({
  type: 'SET_ACTIVE_GROUPED_ITEMS',
  payload: { groupedItems },
});

export const setFixedGroupedItems = (groupedItems) => (dispatch) => {
  dispatch({
    type: 'SET_FIXED_GROUPED_ITEMS',
    payload: { groupedItems }
  });
};

export const setActiveCustomRibbon = (customRibbon) => (dispatch) => {
  fireEvent(Events.TOOLBAR_GROUP_CHANGED, customRibbon);
  dispatch({
    type: 'SET_ACTIVE_CUSTOM_RIBBON',
    payload: { customRibbon }
  });
};

export const setToolbarGroup = (toolbarGroup, pickTool = true, toolGroup = '') => (dispatch, getState) => {
  const getFirstToolGroupForToolbarGroup = (state, _toolbarGroup) => {
    const toolGroups = state.viewer.headers[_toolbarGroup];
    let firstToolGroupForToolbarGroup = '';
    if (toolGroups) {
      const firstTool = Object.values(toolGroups).find(({ toolGroup, dataElement }) => {
        if (toolGroup && !isElementDisabled(state, dataElement) && !allButtonsInGroupDisabled(state, toolGroup)) {
          return true;
        }
        return false;
      });
      if (firstTool) {
        firstToolGroupForToolbarGroup = firstTool.toolGroup;
      }
    }
    return firstToolGroupForToolbarGroup;
  };

  const getFirstToolNameForGroup = (state, toolGroup) => {
    const tools = state.viewer.toolButtonObjects;
    const firstTool = Object.keys(tools).find((key) => {
      return tools[key].group === toolGroup;
    });
    return firstTool;
  };

  if (toolbarGroup === 'toolbarGroup-View') {
    dispatch({
      type: 'SET_ACTIVE_TOOL_GROUP',
      payload: { toolGroup: '', toolbarGroup },
    });
    core.setToolMode(defaultTool);
  } else {
    if (!isOfficeEditorMode()) {
      dispatch(openElements(['toolsHeader']));
    }
    const state = getState();
    const lastPickedToolGroup =
      toolGroup || state.viewer.lastPickedToolGroup[toolbarGroup] || getFirstToolGroupForToolbarGroup(state, toolbarGroup);
    const lastPickedToolName =
      state.viewer.lastPickedToolForGroup[lastPickedToolGroup] || getFirstToolNameForGroup(state, lastPickedToolGroup);
    if (pickTool) {
      if (toolbarGroup === DataElements.EDIT_TOOLBAR_GROUP || toolbarGroup === DataElements.EDIT_TEXT_TOOLBAR_GROUP) {
        dispatch(closeElement(DataElements.STYLE_PANEL));
      }
      dispatch({
        type: 'SET_ACTIVE_TOOL_GROUP',
        payload: { toolGroup: lastPickedToolGroup, toolbarGroup },
      });

      if (lastPickedToolName === 'AnnotationCreateSignature') {
        core.setToolMode(defaultTool);
      } else {
        core.setToolMode(lastPickedToolName);
      }
    } else {
      core.setToolMode(defaultTool);
      dispatch({
        type: 'SET_ACTIVE_TOOL_GROUP',
        payload: { toolGroup: '', toolbarGroup },
      });
    }
  }
  dispatch(closeElements(['toolsOverlay', 'signatureOverlay', 'toolStylePopup']));

  dispatch({
    type: 'SET_TOOLBAR_GROUP',
    payload: { toolbarGroup },
  });
  fireEvent(Events.TOOLBAR_GROUP_CHANGED, toolbarGroup);
};

export const setSelectedStampIndex = (index) => ({
  type: 'SET_SELECTED_STAMP_INDEX',
  payload: { index },
});
export const setLastSelectedStampIndex = (index) => ({
  type: 'SET_LAST_SELECTED_STAMP_INDEX',
  payload: { index },
});
export const setSelectedDisplayedSignatureIndex = (index) => ({
  type: 'SET_SELECTED_DISPLAYED_SIGNATURE_INDEX',
  payload: { index },
});
export const setSavedSignatures = (savedSignatures) => ({
  type: 'SET_SAVED_SIGNATURES',
  payload: { savedSignatures },
});
export const setDisplayedSignaturesFilterFunction = (filterFunction) => ({
  type: 'SET_DISPLAYED_SIGNATURES_FILTER_FUNCTION',
  payload: { filterFunction },
});
export const setSignatureMode = (signatureMode) => ({
  type: 'SET_SIGNATURE_MODE',
  payload: { signatureMode }
});

export const setSavedInitials = (savedInitials) => ({
  type: 'SET_SAVED_INITIALS',
  payload: { savedInitials },
});

export const setSelectedDisplayedInitialsIndex = (index) => ({
  type: 'SET_SELECTED_DISPLAYED_INITIALS_INDEX',
  payload: { index },
});

export const setLeftPanelWidth = (width) => ({
  type: 'SET_LEFT_PANEL_WIDTH',
  payload: { width },
});
export const setSearchPanelWidth = (width) => ({
  type: 'SET_SEARCH_PANEL_WIDTH',
  payload: { width },
});
export const setNotesPanelWidth = (width) => ({
  type: 'SET_NOTES_PANEL_WIDTH',
  payload: { width },
});
export const setComparePanelWidth = (width) => ({
  type: 'SET_COMPARE_PANEL_WIDTH',
  payload: { width },
});
export const setRedactionPanelWidth = (width) => ({
  type: 'SET_REDACTION_PANEL_WIDTH',
  payload: { width },
});
export const setTextEditingPanelWidth = (width) => ({
  type: 'SET_TEXT_EDITING_PANEL_WIDTH',
  payload: { width },
});
export const setWv3dPropertiesPanelWidth = (width) => ({
  type: 'SET_WV3D_PROPERTIES_PANEL_WIDTH',
  payload: { width },
});
export const setWv3dPropertiesPanelModelData = (modelData) => ({
  type: 'SET_WV3D_PROPERTIES_PANEL_MODEL_DATA',
  payload: { modelData },
});
export const setWv3dPropertiesPanelSchema = (schema) => ({
  type: 'SET_WV3D_PROPERTIES_PANEL_SCHEMA',
  payload: { schema },
});
export const setOfficeEditorCursorProperties = (cursorProperties) => ({
  type: 'SET_OFFICE_EDITOR_CURSOR_PROPERTIES',
  payload: { cursorProperties },
});
export const setOfficeEditorSelectionProperties = (selectionProperties) => ({
  type: 'SET_OFFICE_EDITOR_SELECTION_PROPERTIES',
  payload: { selectionProperties },
});
export const setOfficeEditorCanUndo = (canUndo) => ({
  type: 'SET_OFFICE_EDITOR_CAN_UNDO',
  payload: { canUndo },
});
export const setOfficeEditorCanRedo = (canRedo) => ({
  type: 'SET_OFFICE_EDITOR_CAN_REDO',
  payload: { canRedo },
});
export const setOfficeEditorIsReplaceInProgress = (isReplaceInProgress) => ({
  type: 'SET_OFFICE_EDITOR_IS_REPLACE_IN_PROGRESS',
  payload: { isReplaceInProgress },
});
export const addOfficeEditorAvailableFontFace = (fontFace) => ({
  type: 'ADD_OFFICE_EDITOR_AVAILABLE_FONT_FACE',
  payload: { fontFace },
});

export const setDocumentContainerWidth = (width) => ({
  type: 'SET_DOCUMENT_CONTAINER_WIDTH',
  payload: { width },
});
export const setDocumentContainerHeight = (height) => ({
  type: 'SET_DOCUMENT_CONTAINER_HEIGHT',
  payload: { height },
});

export const setGapBetweenHeaderItems = (dataElement, gap) => updateHeaderProperty(dataElement, 'gap', gap);

export const setHeaderJustifyContent = (dataElement, justifyContent) => updateHeaderProperty(dataElement, 'justifyContent', justifyContent);

export const setHeaderMaxWidth = (dataElement, maxWidth) => updateHeaderProperty(dataElement, 'maxWidth', maxWidth);

export const setHeaderMaxHeight = (dataElement, maxHeight) => updateHeaderProperty(dataElement, 'maxHeight', maxHeight);

export const setHeaderStyle = (dataElement, style) => updateHeaderProperty(dataElement, 'style', style);

export const setOpacityOfItem = (dataElement, value) => updateHeaderProperty(dataElement, 'opacity', value);

const updateHeaderProperty = (dataElement, property, value) => ({
  type: 'UPDATE_MODULAR_HEADER',
  payload: {
    dataElement,
    property,
    value,
  }
});

// We may want to expose an API to update the props of any modular component
export const setModularComponentProperty = (property, value, dataElement) => ({
  type: 'SET_MODULAR_COMPONENT_PROPERTY',
  payload: {
    property,
    value,
    dataElement,
  }
});

export const setAllGroupedItemsProperty = (property, value) => ({
  type: 'SET_ALL_GROUPED_ITEMS_PROPERTY',
  payload: {
    property,
    value,
  }
});

export const updateGroupedItems = (groupedItemsDataElement, newGroupedItems) => (dispatch, getState) => {
  const componentsMap = {};
  const existingComponentsMap = getState().viewer.modularComponents;
  const normalizedItems = normalizeItems(newGroupedItems, componentsMap, existingComponentsMap);
  dispatch({
    type: 'UPDATE_GROUPED_ITEMS',
    payload: {
      groupedItemsDataElement,
      normalizedItems,
      componentsMap
    }
  });
};

export const enableAllElements = () => ({
  type: 'ENABLE_ALL_ELEMENTS',
  payload: {},
});

const closeOtherOpenPanelsInSameLocation = (state, dispatch, dataElement) => {
  const genericPanelsInSameLocation = getGenericPanelsOnTheSameLocation(state, dataElement);
  genericPanelsInSameLocation.forEach(({ dataElement: panelElement }) => {
    if (panelElement !== dataElement && isElementOpen(state, panelElement)) {
      dispatch(closeElement(panelElement));
    }
  });
};

export const openElement = (dataElement) => (dispatch, getState) => {
  const state = getState();

  const isElementDisabled = state.viewer.disabledElements[dataElement]?.disabled;
  const isLeftPanelOpen = state.viewer.openElements['leftPanel'];
  const isElementOpen = isDataElementLeftPanel(dataElement, state)
    ? isLeftPanelOpen && state.viewer.activeLeftPanel === dataElement
    : state.viewer.openElements[dataElement];
  const isFlyoutElement = state.viewer.flyoutMap?.[dataElement];
  const isElementDisabledViewOnly = isDisabledViewOnly(state, dataElement);

  if (isFlyoutElement) {
    dispatch(setActiveFlyout(dataElement));
  }

  if (dataElement === panelNames.TABS) {
    const visibleTabPanelTabs = getVisibleTabPanelTabs(state, dataElement);
    const hideTabPanel = visibleTabPanelTabs.length === 0;
    if (hideTabPanel) {
      return;
    }
  }

  if (isElementDisabled || isElementOpen || isElementDisabledViewOnly) {
    return;
  }

  const isGenericPanel = getGenericPanels(state).find((item) => dataElement === item.dataElement);
  if (isGenericPanel) {
    closeOtherOpenPanelsInSameLocation(state, dispatch, dataElement);
  }

  // In the mobile UI, we can have only one panel open at a time
  if (isGenericPanel && isMobile()) {
    const openGenericPanel = getOpenGenericPanel(state);
    if (openGenericPanel) {
      dispatch(closeElement(openGenericPanel));
    }
  }

  if (isDataElementLeftPanel(dataElement, state) && dataElement !== DataElements.NOTES_PANEL) {
    if (!isLeftPanelOpen) {
      dispatch({ type: 'OPEN_ELEMENT', payload: { dataElement: 'leftPanel' } });
      fireEvent(Events.VISIBILITY_CHANGED, { element: 'leftPanel', isVisible: true });
    }
    dispatch(setActiveLeftPanel(dataElement));
  } else {
    dispatch({ type: 'OPEN_ELEMENT', payload: { dataElement } });
    fireEvent(Events.VISIBILITY_CHANGED, { element: dataElement, isVisible: true });

    if (dataElement === 'leftPanel' && !isLeftPanelOpen) {
      fireEvent(Events.VISIBILITY_CHANGED, {
        element: state.viewer.activeLeftPanel,
        isVisible: true,
      });
    }
  }
};
export const openElements = (dataElements) => (dispatch) => {
  if (typeof dataElements === 'string') {
    dispatch(openElement(dataElements));
  } else {
    dataElements.forEach((dataElement) => {
      dispatch(openElement(dataElement));
    });
  }
};
export const closeElement = (dataElement) => (dispatch, getState) => {
  const state = getState();

  const isElementDisabled = state.viewer.disabledElements[dataElement]?.disabled;
  const isFlyoutElement = state.viewer.flyoutMap?.[dataElement];
  const isElementClosed = isDataElementLeftPanel(dataElement, state)
    ? state.viewer.activeLeftPanel !== dataElement
    : !state.viewer.openElements[dataElement];

  if (isElementDisabled || isElementClosed) {
    return;
  }

  if (isDataElementLeftPanel(dataElement, state) && state.viewer.openElements['leftPanel']) {
    dispatch({ type: 'CLOSE_ELEMENT', payload: { dataElement: 'leftPanel' } });
    fireEvent(Events.VISIBILITY_CHANGED, { element: 'leftPanel', isVisible: false });
  } else {
    dispatch({ type: 'CLOSE_ELEMENT', payload: { dataElement } });
    fireEvent(Events.VISIBILITY_CHANGED, { element: dataElement, isVisible: false });

    if (isFlyoutElement) {
      dispatch({
        type: 'SET_ACTIVE_FLYOUT',
        payload: { dataElement: null }
      });
      dispatch({
        type: 'SET_FLYOUT_TOGGLE_ELEMENT',
        payload: { toggleElement: null },
      });
    }
    if (dataElement === DataElements.PAGE_MANIPULATION_OVERLAY) {
      dispatch({
        type: 'SET_PAGE_MANIPULATION_OVERLAY_ALTERNATIVE_POSITION',
        payload: null
      });
    }

    if (dataElement === 'leftPanel' && state.viewer.openElements['leftPanel']) {
      fireEvent(Events.VISIBILITY_CHANGED, {
        element: state.viewer.activeLeftPanel,
        isVisible: false,
      });
    }
  }
};
export const closeElements = (dataElements) => (dispatch) => {
  if (typeof dataElements === 'string') {
    dispatch(closeElement(dataElements));
  } else {
    dataElements.forEach((dataElement) => {
      dispatch(closeElement(dataElement));
    });
  }
};

const rightPanelList = ['searchPanel', DataElements.NOTES_PANEL, 'comparePanel', 'redactionPanel', 'wv3dPropertiesPanel', 'textEditingPanel'];
export const toggleElement = (dataElement) => (dispatch, getState) => {
  const state = getState();

  if (isElementDisabled(state, dataElement)) {
    return;
  }

  if (getIsCustomUIEnabled(state)) {
    const isGenericPanel = getGenericPanels(state).find((item) => dataElement === item.dataElement);
    isGenericPanel && closeOtherOpenPanelsInSameLocation(state, dispatch, dataElement);
  }

  if (!state.viewer.notesInLeftPanel) {
    if (rightPanelList.includes(dataElement)) {
      for (const panel of rightPanelList) {
        if (panel !== dataElement) {
          dispatch(closeElement(panel));
        }
      }
    }
  }

  if (state.viewer.openElements[dataElement]) {
    dispatch(closeElement(dataElement));
  } else {
    dispatch(openElement(dataElement));
  }
};

export const addCustomModal = (modalOptions) => ({
  type: 'ADD_CUSTOM_MODAL',
  payload: modalOptions,
});

const headerKeysToStore = [
  'dataElement', 'label', 'placement', 'justifyContent',
  'grow', 'gap', 'position', 'float', 'maxWidth', 'maxHeight',
  'opacityMode', 'opacity', 'stroke', 'dimension', 'position', 'style'
];

// These are all the keys from the items we will store
// There are a few non serializable ones here like the onclick function
// we can determine their fate later
const itemKeysToStore = [
  'dataElement', 'items', 'title', 'disabled', 'type',
  'isActive', 'label', 'img', 'onClick', 'justifyContent',
  'groupedItems', 'grow', 'gap', 'position', 'placement', 'alwaysVisible',
  'style', 'headerDirection', 'icon', 'toolbarGroup', 'direction',
  'states', 'mount', 'unmount', 'initialState', 'hidden', 'toggleElement',
  'toolName', 'color', 'buttonType', 'render', 'renderArguments', 'className'];

//* Recursively normalize the items in a header
const normalizeItems = (items, componentsMap, existingComponentsMap) => {
  const result = [];
  for (let i = 0, len = items.length; i < len; i++) {
    const item = items[i];
    const normalizedItem = pick(item, itemKeysToStore);
    const dataElementKey = normalizedItem.dataElement;

    // If there are nested items, recursively normalize them
    if (item.items?.length > 0) {
      const nestedItemsDataElements = normalizeItems(item.items, componentsMap, existingComponentsMap);
      normalizedItem.items = nestedItemsDataElements;
    }

    const componentsMapHasItem = componentsMap[dataElementKey];
    // If existing the component items already contains dataElementKey and there is a difference in the new component, merge; otherwise append as new.
    if (componentsMapHasItem) {
      const areComponentsDifferent = !areConfigsEquivalent(item, existingComponentsMap[dataElementKey]);
      if (areComponentsDifferent) {
        const existingComponent = existingComponentsMap[dataElementKey];
        componentsMap[dataElementKey] = { ...existingComponent, ...normalizedItem };
        console.warn(`Modular component with dataElement ${dataElementKey} already exists. Existing component's properties have been updated.`);
      }
    }

    componentsMap[dataElementKey] = normalizedItem;
    result.push(dataElementKey);
  }
  return result;
};

const prepareModularHeaders = (headersList, existingComponentsMap = {}) => {
  const headersMap = {};
  const componentsMap = {};

  headersList = Array.isArray(headersList) ? headersList : [headersList];
  headersList.forEach((header) => {
    if (headersMap[header.dataElement]) {
      const newKey = `${header.dataElement}-${uuidv4()}`;
      console.warn(`Modular header with dataElement ${header.dataElement} already exists. Appending unique ID to prevent collisions: ${newKey}`);
      header.dataElement = newKey;
    }

    const itemDataElements = normalizeItems(header.items, componentsMap, existingComponentsMap);
    headersMap[header.dataElement] = { ...pick(header, headerKeysToStore), items: itemDataElements };
  });
  return { headersMap, componentsMap };
};

export const addModularHeaders = (headersList) => (dispatch, getState) => {
  const existingComponentsMap = getState().viewer.modularComponents;
  const { headersMap, componentsMap } = prepareModularHeaders(headersList, existingComponentsMap);

  checkFeaturesToEnable(componentsMap);

  dispatch({
    type: 'ADD_MODULAR_HEADERS_AND_COMPONENTS',
    payload: { headersMap, componentsMap }
  });
};
export const setModularHeaders = (headersList) => (dispatch) => {
  const { headersMap, componentsMap } = prepareModularHeaders(headersList);
  dispatch({
    type: 'SET_MODULAR_HEADERS_AND_COMPONENTS',
    payload: { headersMap, componentsMap }
  });
};


export const setModularHeaderItems = (headerDataElement, items) => (dispatch, getState) => {
  const existingComponentsMap = getState().viewer.modularComponents;
  const newComponentsMap = {};
  const itemsDataElements = normalizeItems(items, newComponentsMap, existingComponentsMap);

  dispatch({
    type: 'SET_MODULAR_HEADER_ITEMS',
    payload: {
      headerDataElement: headerDataElement,
      normalizedItems: newComponentsMap,
      itemsDataElements: itemsDataElements
    }
  });
};

export const setModularHeadersAndComponents = (headersMap, componentsMap) => (dispatch) => {
  dispatch({
    type: 'SET_MODULAR_HEADERS_AND_COMPONENTS',
    payload: { headersMap, componentsMap }
  });
};

export const setModularComponentFunctions = (functionMap) => (dispatch) => {
  dispatch({
    type: 'SET_MODULAR_COMPONENT_FUNCTIONS',
    payload: { functionMap }
  });
};

export const setFlyouts = (flyouts) => (dispatch) => {
  dispatch({
    type: 'SET_FLYOUTS',
    payload: { flyouts }
  });
};

export const openFlyout = (dataElement, toggleElement) => (dispatch, getState) => {
  const state = getState();
  const isElementDisabled = state.viewer.disabledElements[dataElement]?.disabled;
  const flyoutElement = state.viewer.flyoutMap?.[dataElement];
  const toggleComponent = flyoutElement?.toggleElement ?? toggleElement;
  const isToggleElementDisabled = state.viewer.disabledElements[toggleComponent]?.disabled;
  if (isElementDisabled || !flyoutElement || isToggleElementDisabled) {
    return;
  }

  if (toggleComponent) {
    const ribbonAssociatedWithToggleButton = selectors.getRibbonAssociatedWithToggleButton(state, toggleComponent);
    const activeCustomRibbon = selectors.getActiveCustomRibbon(state);
    if (
      ribbonAssociatedWithToggleButton &&
      activeCustomRibbon !== ribbonAssociatedWithToggleButton
    ) {
      dispatch(setActiveCustomRibbon(ribbonAssociatedWithToggleButton));
    }

    dispatch(setFlyoutToggleElement(toggleComponent));
    dispatch(openElement(dataElement));
  } else {
    console.warn(`No toggle element provided for flyout ${dataElement}. Please provide a toggle element to open the flyout.`);
  }
};

export const resetModularUIState = () => ({
  type: 'RESET_MODULAR_UI_STATE',
});
export const setHeaderWidth = (header, width) => ({
  type: 'SET_HEADER_WIDTH',
  payload: { header, width }
});
export const setTopFloatingContainerHeight = (height) => ({
  type: 'SET_TOP_FLOATING_CONTAINER_HEIGHT',
  payload: height
});
export const setBottomFloatingContainerHeight = (height) => ({
  type: 'SET_BOTTOM_FLOATING_CONTAINER_HEIGHT',
  payload: height
});

export const setActiveLeftPanel = (dataElement) => (dispatch, getState) => {
  const state = getState();

  if (isDataElementLeftPanel(dataElement, state)) {
    if (state.viewer.activeLeftPanel !== dataElement) {
      dispatch({
        type: 'CLOSE_ELEMENT',
        payload: { dataElement: state.viewer.activeLeftPanel },
      });
      fireEvent(Events.VISIBILITY_CHANGED, {
        element: state.viewer.activeLeftPanel,
        isVisible: false,
      });
      dispatch({ type: 'SET_ACTIVE_LEFT_PANEL', payload: { dataElement } });
      fireEvent(Events.VISIBILITY_CHANGED, { element: dataElement, isVisible: true });
    }
  } else {
    const panelDataElements = [
      ...state.viewer.customPanels.map(({ panel }) => panel.dataElement),
      DataElements.PORTFOLIO_PANEL,
      'thumbnailsPanel',
      'outlinesPanel',
      'layersPanel',
      'bookmarksPanel',
      DataElements.NOTES_PANEL,
      'signaturePanel',
      'attachmentPanel',
    ].join(', ');
    console.warn(
      `${dataElement} is not recognized by the left panel. Please use one of the following options: ${panelDataElements}`,
    );
  }
};

export const setActiveHeaderGroup = (headerGroup) => ({
  type: 'SET_ACTIVE_HEADER_GROUP',
  payload: { headerGroup },
});

export const setTimezone = (timezone) => ({
  type: 'SET_TIMEZONE',
  payload: { timezone },
});
export const setNotesPanelSortStrategy = (sortStrategy) => ({
  type: 'SET_NOTES_PANEL_SORT_STRATEGY',
  payload: { sortStrategy },
});

export const setNoteDateFormat = (noteDateFormat) => ({
  type: 'SET_NOTE_DATE_FORMAT',
  payload: { noteDateFormat },
});
export const setPrintedNoteDateFormat = (noteDateFormat) => ({
  type: 'SET_PRINTED_NOTE_DATE_FORMAT',
  payload: { noteDateFormat },
});
export const setCustomPanel = (newPanel) => ({
  type: 'SET_CUSTOM_PANEL',
  payload: { newPanel },
});

export const addPanel = (newPanel) => ({
  type: 'ADD_PANEL',
  payload: { newPanel },
});

export const setGenericPanels = (genericPanels) => ({
  type: 'SET_GENERIC_PANELS',
  payload: { genericPanels },
});

export const setMobilePanelSize = (panelSize) => ({
  type: 'SET_MOBILE_PANEL_SIZE',
  payload: { panelSize },
});

export const setPageLabels = (pageLabels, documentViewerKey = 1) => (dispatch) => {
  if (pageLabels.length !== core.getTotalPages(documentViewerKey)) {
    console.warn('Number of page labels do not match with the total pages.');
    return;
  }
  dispatch({
    type: 'SET_PAGE_LABELS',
    payload: {
      pageLabels: pageLabels.map(String),
      documentViewerKey
    },
  });
};
export const setSelectedPageThumbnails = (selectedThumbnailPageIndexes = []) => {
  fireEvent(Events.SELECTED_THUMBNAIL_CHANGED, selectedThumbnailPageIndexes);

  return {
    type: 'SET_SELECTED_THUMBNAIL_PAGE_INDEXES',
    payload: { selectedThumbnailPageIndexes },
  };
};
export const setShiftKeyThumbnailsPivotIndex = (shiftKeyThumbnailPivotIndex = null) => ({
  type: 'SET_SHIFT_KEY_THUMBNAIL_PIVOT_INDEX',
  payload: { shiftKeyThumbnailPivotIndex },
});
export const setSwipeOrientation = (swipeOrientation) => ({
  type: 'SET_SWIPE_ORIENTATION',
  payload: { swipeOrientation },
});
export const showWarningMessage = (options) => (dispatch) => {
  dispatch({ type: 'SET_WARNING_MESSAGE', payload: options });
  dispatch(openElement('warningModal'));
};
export const enableDeleteTabWarning = () => ({
  type: 'ENABLE_DELETE_TAB_WARNING',
  payload: { showDeleteTabWarning: true },
});
export const disableDeleteTabWarning = () => ({
  type: 'DISABLE_DELETE_TAB_WARNING',
  payload: { showDeleteTabWarning: false },
});
export const showErrorMessage = (message, title = '') => (dispatch) => {
  dispatch({ type: 'SET_ERROR_MESSAGE', payload: { message, title } });
  dispatch(openElement('errorModal'));
};
export const setCustomNoteFilter = (filterFunc) => ({
  type: 'SET_CUSTOM_NOTE_FILTER',
  payload: { customNoteFilter: filterFunc },
});
export const setInternalNoteFilter = (filterFunc) => ({
  type: 'SET_INTERNAL_NOTE_FILTER',
  payload: { internalNoteFilter: filterFunc },
});
export const setInlineCommentFilter = (filterFunc) => ({
  type: 'SET_INLINE_COMMENT_FILTER',
  payload: { inlineCommentFilter: filterFunc },
});
export const setZoomList = (zoomList) => (dispatch) => {
  const minZoomLevel = getMinZoomLevel();
  const maxZoomLevel = getMaxZoomLevel();
  const filteredZoomList = zoomList.filter((zoom) => zoom >= minZoomLevel && zoom <= maxZoomLevel);

  if (filteredZoomList.length !== zoomList.length) {
    const outOfRangeZooms = zoomList.filter((zoom) => !filteredZoomList.includes(zoom));
    console.warn(`
      ${outOfRangeZooms.join(', ')} are not allowed zoom levels in the UI.
      Valid zoom levels should be in the range of ${minZoomLevel}-${maxZoomLevel}.
      You can use setMinZoomLevel or setMaxZoomLevel APIs to change the range.
      See https://docs.apryse.com/api/web/UI.html for more information.
    `);
  }

  dispatch({ type: 'SET_ZOOM_LIST', payload: { zoomList: filteredZoomList } });
};
export const useEmbeddedPrint = (useEmbeddedPrint = true) => ({
  type: 'USE_EMBEDDED_PRINT',
  payload: { useEmbeddedPrint },
});
export const useClientSidePrint = (useClientSidePrint = true) => ({
  type: 'USE_CLIENT_SIDE_PRINT',
  payload: { useClientSidePrint },
});
export const setMaxSignaturesCount = (maxSignaturesCount) => ({
  type: 'SET_MAX_SIGNATURES_COUNT',
  payload: { maxSignaturesCount },
});
export const setUserData = (userData) => ({
  type: 'SET_USER_DATA',
  payload: { userData },
});
export const setCustomMeasurementOverlay = (customMeasurementOverlay) => ({
  type: 'SET_CUSTOM_MEASUREMENT_OVERLAY',
  payload: { customMeasurementOverlay },
});
export const setSelectedTab = (id, dataElement) => ({
  type: 'SET_SELECTED_TAB',
  payload: { id, dataElement },
});
export const setCustomElementOverrides = (dataElement, overrides) => ({
  type: 'SET_CUSTOM_ELEMENT_OVERRIDES',
  payload: { dataElement, overrides },
});
export const setPageReplacementModalFileList = (list) => ({
  type: 'SET_PAGE_REPLACEMENT_FILE_LIST',
  payload: { list },
});
export const setActiveTheme = (theme) => {
  fireEvent(Events.THEME_CHANGED, theme);

  return {
    type: 'SET_ACTIVE_THEME',
    payload: { theme },
  };
};
export const setSearchStatus = (status) => ({
  type: 'SET_SEARCH_STATUS',
  payload: status,
});
export const setSearchResults = (searchResults) => ({
  type: 'SET_SEARCH_RESULTS',
  payload: searchResults,
});

export const setClearSearchOnPanelClose = (shouldClear) => ({
  type: 'SET_CLEAR_SEARCH_ON_PANEL_CLOSE',
  payload: shouldClear,
});

export const setAnnotationContentOverlayHandler = (annotationContentOverlayHandler) => ({
  type: 'SET_ANNOTATION_CONTENT_OVERLAY_HANDLER',
  payload: { annotationContentOverlayHandler },
});

export const setAnnotationReadState = ({ isRead, annotationId }) => ({
  type: 'SET_ANNOTATION_READ_STATE',
  payload: { isRead, annotationId },
});
export const addTrustedCertificates = (certificates, documentViewerKey = 1) => ({
  type: 'ADD_TRUSTED_CERTIFICATES',
  payload: { certificates, documentViewerKey },
});
export const setTrustListKey = (trustListKey) => ({
  type: 'SET_TRUST_LIST_KEY',
  payload: { trustListKey },
});
export const setSignatureValidationModalWidgetName = (widgetName) => ({
  type: 'SET_VALIDATION_MODAL_WIDGET_NAME',
  payload: { validationModalWidgetName: widgetName },
});

export const setNoteSubmissionEnabledWithEnter = (enableNoteSubmissionWithEnter) => ({
  type: 'SET_SUBMIT_COMMENT_MODE',
  payload: { enableNoteSubmissionWithEnter },
});

export const setNotesPanelTextCollapsing = (enableNotesPanelTextCollapsing) => ({
  type: 'SET_NOTES_PANEL_TEXT_COLLAPSING',
  payload: { enableNotesPanelTextCollapsing },
});

export const setNotesPanelRepliesCollapsing = (enableNotesPanelRepliesCollapsing) => ({
  type: 'SET_NOTES_PANEL_REPLIES_COLLAPSING',
  payload: { enableNotesPanelRepliesCollapsing },
});

export const setCommentThreadExpansion = (enableCommentThreadExpansion) => ({
  type: 'SET_COMMENT_THREAD_EXPANSION',
  payload: { enableCommentThreadExpansion },
});

export const enableFadePageNavigationComponent = () => (dispatch) => {
  dispatch(setOpacityOfItem(DataElements.PAGE_NAV_FLOATING_HEADER, OPACITY_LEVELS.NONE));
  return dispatch({
    type: 'SET_FADE_PAGE_NAVIGATION_COMPONENT',
    payload: { fadePageNavigationComponent: true },
  });
};

export const disableFadePageNavigationComponent = () => (dispatch) => {
  dispatch(setOpacityOfItem(DataElements.PAGE_NAV_FLOATING_HEADER, OPACITY_LEVELS.FULL));
  return dispatch({
    type: 'SET_FADE_PAGE_NAVIGATION_COMPONENT',
    payload: { fadePageNavigationComponent: false },
  });
};

export const enablePageDeletionConfirmationModal = () => ({
  type: 'PAGE_DELETION_CONFIRMATION_MODAL_POPUP',
  payload: { pageDeletionConfirmationModalEnabled: true },
});

export const disablePageDeletionConfirmationModal = () => ({
  type: 'PAGE_DELETION_CONFIRMATION_MODAL_POPUP',
  payload: { pageDeletionConfirmationModalEnabled: false },
});

export const setWatermarkModalOptions = (watermarkModalOptions) => ({
  type: 'SET_WATERMARK_MODAL_OPTIONS',
  payload: { watermarkModalOptions },
});
export const setEmbeddedPopupMenuStyle = (customStyle) => ({
  type: 'SET_EMBEDDED_JS_POPUP_MENU_STYLE',
  payload: { embeddedJSPopupStyle: customStyle },
});
export const replaceRedactionSearchPattern = (searchPattern, regex) => ({
  type: 'REPLACE_REDACTION_SEARCH_PATTERN',
  payload: { searchPattern, regex },
});

export const setThumbnailSelectionMode = (thumbnailSelectionMode) => ({
  type: 'SET_THUMBNAIL_SELECTION_MODE',
  payload: { thumbnailSelectionMode },
});

export const addRedactionSearchPattern = (searchPattern) => ({
  type: 'ADD_REDACTION_SEARCH_PATTERN',
  payload: { searchPattern },
});

export const removeRedactionSearchPattern = (searchPatternType) => ({
  type: 'REMOVE_REDACTION_SEARCH_PATTERN',
  payload: { searchPatternType },
});

export const setColorMap = (colorMap) => ({
  type: 'SET_COLOR_MAP',
  payload: { colorMap }
});

export const setZoomStepFactors = (zoomStepFactors) => ({
  type: 'SET_ZOOM_STEP_FACTORS',
  payload: { zoomStepFactors },
});

export const setNotesPanelCustomHeader = (notesPanelCustomHeaderOptions) => ({
  type: 'SET_NOTES_PANEL_CUSTOM_HEADER_OPTIONS',
  payload: { notesPanelCustomHeaderOptions },
});

export const setNotesPanelEmptyPanel = (notesPanelCustomEmptyPanel) => ({
  type: 'SET_NOTES_PANEL_CUSTOM_EMPTY_PANEL',
  payload: { notesPanelCustomEmptyPanel },
});

export const addMeasurementScalePreset = (measurementSystem, newPreset, index) => ({
  type: 'ADD_MEASUREMENT_SCALE_PRESET',
  payload: { measurementSystem, newPreset, index }
});

export const removeMeasurementScalePreset = (measurementSystem, index) => ({
  type: 'REMOVE_MEASUREMENT_SCALE_PRESET',
  payload: { measurementSystem, index }
});

export const setIsMultipleScalesMode = (isMultipleScalesMode) => ({
  type: 'SET_IS_MULTIPLE_SCALE_MODE',
  payload: { isMultipleScalesMode }
});

export const setReplyAttachmentPreviewEnabled = (replyAttachmentPreviewEnabled) => ({
  type: 'SET_REPLY_ATTACHMENT_PREVIEW',
  payload: { replyAttachmentPreviewEnabled }
});

export const setNotesPanelMultiSelect = (isNotesPanelMultiSelectEnabled) => ({
  type: 'SET_NOTES_PANEL_MULTI_SELECT',
  payload: { isNotesPanelMultiSelectEnabled }
});

export const setReplyAttachmentHandler = (replyAttachmentHandler) => ({
  type: 'SET_REPLY_ATTACHMENT_HANDLER',
  payload: { replyAttachmentHandler }
});

export const setCustomSettings = (customSettings) => ({
  type: 'SET_CUSTOM_SETTINGS',
  payload: customSettings
});

export const setEnableRightClickAnnotationPopup = (isEnabled) => ({
  type: 'SET_ENABLE_RIGHT_CLICK_ANNOTATION_POPUP',
  payload: { isEnabled }
});

export const setToolDefaultStyleUpdateFromAnnotationPopupEnabled = (isToolDefaultStyleUpdateFromAnnotationPopupEnabled) => ({
  type: 'SET_TOOL_DEFAULT_STYLE_UPDATE_FROM_ANNOTATION_POPUP_ENABLED',
  payload: isToolDefaultStyleUpdateFromAnnotationPopupEnabled
});

export const setAnnotationToolStyleSyncingEnabled = (isAnnotationToolStyleSyncingEnabled) => ({
  type: 'SET_ANNOTATION_TOOL_STYLE_SYNCING_ENABLED',
  payload: isAnnotationToolStyleSyncingEnabled
});

export const setMultiViewerSyncScrollingMode = (multiViewerComparedSyncScrollingMode) => ({
  type: 'SET_MULTI_VIEWER_SYNC_SCROLLING_MODE',
  payload: multiViewerComparedSyncScrollingMode
});

export const setTextSignatureQuality = (multiplier) => ({
  type: 'SET_TEXT_SIGNATURE_CANVAS_MULTIPLIER',
  payload: { multiplier },
});

export const setEnableMeasurementAnnotationsFilter = (isEnabled) => ({
  type: 'SET_ENABLE_MEASUREMENT_ANNOTATIONS_FILTER',
  payload: { isEnabled },
});

export const setColors = (colors, tool, type, updateOnly = false) => (dispatch, getState) => {
  type = type ? type.toLowerCase() : type;
  const state = getState();
  if (tool && (!updateOnly || (updateOnly && state.viewer.toolColorOverrides[tool]))) {
    return dispatch({
      type: 'SET_COLORS',
      payload: { tool, colors },
    });
  }
  dispatch({
    type: 'SET_COLORS',
    payload: type === 'text' ? { textColors: colors } : { colors },
  });
};

export const setAccessibleMode = (isAccessibleMode) => ({
  type: 'SET_ACCESSIBLE_MODE',
  payload: { isAccessibleMode },
});

export const setShouldAddA11yContentToDOM = (shouldAddA11yContentToDOM) => ({
  type: 'SET_SHOULD_ADD_A11Y_CONTENT',
  payload: { shouldAddA11yContentToDOM },
});

export const setSpreadsheetEditorEditMode = (mode) => ({
  type: 'SET_SPREADSHEET_EDITOR_EDIT_MODE',
  payload: { mode },
});

export const enableWidgetHighlighting = () => ({
  type: 'ENABLE_WIDGET_HIGHLIGHTING',
});
export const disableWidgetHighlighting = () => ({
  type: 'DISABLE_WIDGET_HIGHLIGHTING',
});

export const setCellBackgroundColors = (colors) => ({
  type: 'SET_CELL_BACKGROUND_COLORS',
  payload: { colors },
});
export const setCustomCellBackgroundColors = (customColors) => ({
  type: 'SET_CUSTOM_CELL_BACKGROUND_COLORS',
  payload: { customColors },
});
export const setTextColors = (colors) => ({
  type: 'SET_TEXT_COLORS',
  payload: { colors },
});
export const setCustomTextColors = (customColors) => ({
  type: 'SET_CUSTOM_TEXT_COLORS',
  payload: { customColors },
});
export const setBorderColors = (colors) => ({
  type: 'SET_BORDER_COLORS',
  payload: { colors },
});
export const setCustomBorderColors = (customColors) => ({
  type: 'SET_CUSTOM_BORDER_COLORS',
  payload: { customColors },
});